import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { dsApi, dsDtos } from '@vectronic/openApi-admin';
import { catchError, finalize, forkJoin, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ParameterAdminService {
  private parameterAdminApi = inject(dsApi.ParametersAdmin);
  private parameterCustomerApi = inject(dsApi.ParametersCustomer);
  private subsystemsApi = inject(dsApi.Subsystems);
  private parameterCategoryApi = inject(dsApi.ParametersCategories);

  parameters = signal<dsDtos.Parameter[]>([]);
  parameterSubsystems = signal<dsDtos.Subsystem[]>([]);
  parameterCategories = signal<dsDtos.ParameterCategory[]>([]);
  isLoading = signal(false);

  parameters$ = toObservable(this.parameters);
  parameterSubsystems$ = toObservable(this.parameterSubsystems);
  parameterCategories$ = toObservable(this.parameterCategories);

  constructor() {
    this.getParameters();
  }

  getParameters() {
    this.isLoading.set(true);
    forkJoin({
      parameters: this.parameterCustomerApi.getParameters().pipe(catchError(() => of([]))),
      subsystems: this.subsystemsApi.getSubsystems().pipe(catchError(() => of([]))),
      categories: this.parameterCategoryApi.getParameterCategories().pipe(catchError(() => of([]))),
    })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((res) => {
        this.parameters.set(res.parameters);
        this.parameterSubsystems.set(res.subsystems);
        this.parameterCategories.set(res.categories);
      });
  }

  addParameter(parameter: dsDtos.Parameter) {
    return this.parameterAdminApi.addParameter(parameter).pipe(
      tap((res) => {
        this.parameters.update((params) => [...params, res]);
      }),
    );
  }

  editParameter(id: string, parameter: dsDtos.Parameter) {
    return this.parameterAdminApi.editParameter(id, parameter).pipe(
      tap((res) => {
        this.parameters.update((params) => params.map((p) => (p.id === id ? res : p)));
      }),
    );
  }

  deleteParameter(id: string) {
    return this.parameterAdminApi.removeParameter(id).pipe(
      tap(() => {
        this.parameters.update((params) => params.filter((p) => p.id !== id));
      }),
    );
  }
}
